import Head from 'next/head'
import styles from '../../styles/index.module.css'
import { About } from '../../components/index/About';
import Script from 'next/script';
import { ORGANIZATION_ID, SITE, localBusinessNode, organizationNode } from '../../utils/local-business-schema'

// The one company About page for every country. The UAE and Malaysia pages
// link "About Us" to their own section of it (#uae, #malaysia - aboutPath in
// utils/markets.js), so it describes StringsSG as a whole rather than the
// Singapore business alone.

const PAGE_URL = `${SITE}/about-stringssg`
const TITLE = 'About StringsSG | Home Services in Singapore, UAE & Malaysia'
const DESCRIPTION = 'StringsSG has delivered home services since 2018: aircon servicing, personal trainers, handyman and part-time helpers in Singapore, and AC / aircond servicing and personal trainers in Dubai, Abu Dhabi and Kuala Lumpur.'
const OG_IMAGE = 'https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/Google-icon.png'

const faqs = [
  {
    question: 'Which countries does StringsSG serve?',
    answer: 'Singapore, the United Arab Emirates (Dubai and Abu Dhabi) and Malaysia (Kuala Lumpur and the Klang Valley). Singapore has aircon servicing, personal trainers, handyman, electrician and part-time helper services; the UAE has AC servicing and Malaysia aircond servicing, both with personal trainers.',
  },
  {
    question: 'Are all services on StringsSG handled by your team?',
    answer: 'Not all. Our core services (aircon servicing, handyman, part-time helpers, and personal trainers) are delivered to StringsSG service standards by vetted professionals or, in Singapore, our directly hired trainers. Other services are lead-based and handled by external professionals.',
  },
  {
    question: 'How do you verify the professionals who deliver StringsSG services?',
    answer: 'We onboard only trusted providers after strict checks on qualifications, experience, and reliability, in every country we serve, ensuring peace of mind for our customers.',
  },
  {
    question: 'What makes StringsSG’s personal trainers different?',
    answer: 'In Singapore, StringsSG directly hires personal trainers, which means reliable scheduling, consistent quality, and better accountability for clients. In the UAE and Malaysia, every trainer is checked before their first session and works to the same StringsSG standards.',
  },
  {
    question: 'How do I book a service?',
    answer: 'Choose your country, pick a service, and book online with the price shown upfront. You can also track sessions, make payments, and communicate directly within StringsSG.',
  },
]

const aboutSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'AboutPage',
      '@id': `${PAGE_URL}/#webpage`,
      url: PAGE_URL,
      name: TITLE,
      description: DESCRIPTION,
      about: { '@id': ORGANIZATION_ID },
    },
    {
      ...organizationNode,
      areaServed: [
        { '@type': 'Country', name: 'Singapore' },
        { '@type': 'Country', name: 'United Arab Emirates' },
        { '@type': 'Country', name: 'Malaysia' },
      ],
    },
    localBusinessNode,
    {
      '@type': 'FAQPage',
      '@id': `${PAGE_URL}/#faq`,
      mainEntity: faqs.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    },
  ],
}

export default function Home() {

  return (
    <>
      <Head>
        <title>{TITLE}</title>
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no" />
        <meta name="description" content={DESCRIPTION} />
        <meta name="author" content="StringsSG" />
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <link rel="canonical" href={PAGE_URL} />

        {/* Open Graph */}
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="twitter:site" content="@stringssg" />
        <meta name="twitter:url" content={PAGE_URL} />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />

        <link rel="icon" href="/favicon.ico" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
        />

      </Head>

      <div className={styles.main}>
        <About />
      </div>

      {/* Tracking Scripts */}
      <Script
        id="google-tagmanager-new"
        src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
        strategy="lazyOnload"
        onLoad={() => {
          window.dataLayer = window.dataLayer || [];
          function gtag() { dataLayer.push(arguments); }
          gtag('js', new Date());
          gtag('config', 'G-QD1HGGHDYJ');
        }}
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
        {`!function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '128634041116395');
          fbq('track', 'PageView');`}
      </Script>
    </>
  )
}
